/** @jsxImportSource @emotion/react */
import { useRef, useState } from "react";
import { Tag } from "primereact/tag";
import { Divider } from "primereact/divider";
import { Toast } from "primereact/toast";
import { Boton, Titulo, CampoInformacion, downloadFile } from "../../../shared";

// Vista de solo lectura del Análisis de IA (admin). Muestra la decisión, el
// texto del análisis, los documentos generados y los productos propuestos con
// sus campos propios y características críticas.
export default function AnalisisIA({ analisis, proceso }) {
  const toast = useRef(null);
  const [descargandoId, setDescargandoId] = useState(null);
  const [loaderExcelProductos, setLoaderExcelProductos] = useState(false);

  // Descarga los productos propuestos en un Excel con el formato de la
  // plantilla de carga masiva
  function descargarExcelProductos() {
    setLoaderExcelProductos(true);
    // Arma el nombre del archivo con el cliente y el proceso, sin caracteres
    // que no puedan ir en un nombre de archivo
    const nombreArchivo = [
      "Productos Análisis IA",
      proceso?.cliente?.razonsocial,
      proceso?.nombre,
    ]
      .filter(Boolean)
      .join(" - ")
      .replace(/[\\/:*?"<>|]/g, "-");
    downloadFile(
      `admin/proceso/analisis/${analisis.id}/productos/excel`,
      `${nombreArchivo}.xlsx`,
      () => setLoaderExcelProductos(false),
      (r) => {
        setLoaderExcelProductos(false);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: r?.[0]?.msg || "No se pudo descargar el archivo",
          life: 4000
        });
      }
    );
  }

  function descargar(doc) {
    setDescargandoId(doc.archivoId);
    downloadFile(
      `archivo/${doc.hash}/download`,
      doc.nombredescarga || `archivo-${doc.archivoId}`,
      () => setDescargandoId(null),
      (r) => {
        setDescargandoId(null);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: r?.[0]?.msg || "No se pudo descargar el archivo",
          life: 4000
        });
      }
    );
  }

  if (!analisis) {
    return (
      <>
        <Titulo>Análisis de IA</Titulo>
        <p css={{ color: "var(--grisTextos)" }}>
          No hay un análisis de IA para este proceso.
        </p>
      </>
    );
  }

  const tagEstado = (estado) => {
    const map = {
      volcado: { severity: "success", value: "Volcado" },
      descartado: { severity: "danger", value: "Descartado" },
      pendiente: { severity: "warning", value: "Pendiente" }
    };
    const t = map[estado] || { severity: "info", value: estado };
    return <Tag severity={t.severity} value={t.value} />;
  };

  const documentos = Array.isArray(analisis.documentosGenerados)
    ? analisis.documentosGenerados
    : [];

  return (
    <>
      <Toast ref={toast} position="top-center" />
      <Titulo>Análisis de IA</Titulo>

      {analisis.decision && (
        <div className="grid">
          <CampoInformacion nombreCampo="Decisión" anchoCompleto>
            <span css={{ whiteSpace: "pre-wrap" }}>{analisis.decision}</span>
          </CampoInformacion>
        </div>
      )}

      {analisis.texto && (
        <div className="grid">
          <CampoInformacion nombreCampo="Análisis" anchoCompleto>
            <span css={{ whiteSpace: "pre-wrap" }}>{analisis.texto}</span>
          </CampoInformacion>
        </div>
      )}

      {documentos.length > 0 && (
        <>
          <Divider />
          <p css={{ color: "var(--grisTextos)", marginBottom: "0.5em" }}>
            Documentos generados
          </p>
          <div css={{ display: "flex", flexWrap: "wrap", gap: "0.5em" }}>
            {documentos.map((doc) => (
              <Boton
                key={doc.archivoId}
                transparente
                loader={descargandoId === doc.archivoId}
                callback={() => descargar(doc)}
              >
                <i className="pi pi-download" /> {doc.nombredescarga}
              </Boton>
            ))}
          </div>
        </>
      )}

      <Divider />
      <Titulo
        botones={
          (analisis.productosPropuestos?.length || 0) > 0 && (
            <Boton
              transparente
              loader={loaderExcelProductos}
              callback={descargarExcelProductos}
            >
              <i className="pi pi-download" /> Descargar Excel
            </Boton>
          )
        }
      >
        Productos propuestos ({analisis.productosPropuestos?.length || 0})
      </Titulo>

      {(analisis.productosPropuestos || []).map((producto, idx) => {
        const campos = [...(producto.campos || [])].sort(
          (a, b) =>
            (a.productocampospropios?.orden || 0) -
            (b.productocampospropios?.orden || 0)
        );
        const caracteristicas = [...(producto.caracteristicas || [])].sort(
          (a, b) =>
            (a.caracteristicacritica?.orden || 0) -
            (b.caracteristicacritica?.orden || 0)
        );
        return (
          <div key={producto.id} css={{ marginBottom: "1em" }}>
            {idx > 0 && <Divider />}
            <div
              css={{
                display: "flex",
                justifyContent: "flex-end",
                marginBottom: "0.5em"
              }}
            >
              {tagEstado(producto.estado)}
            </div>
            <div className="grid">
              {campos.map((campo) => (
                <CampoInformacion
                  key={campo.id}
                  nombreCampo={campo.productocampospropios?.etiqueta}
                >
                  {campo.valor
                    ? `${campo.valor}${
                        campo.productocampospropios?.unidades
                          ? " " + campo.productocampospropios.unidades
                          : ""
                      }`
                    : "-"}
                </CampoInformacion>
              ))}
            </div>

            {caracteristicas.length > 0 && (
              <>
                <Divider />
                <p css={{ color: "var(--grisTextos)", marginBottom: "0.5em" }}>
                  Características críticas
                </p>
                <div className="grid">
                  {caracteristicas.map((carac) => (
                    <CampoInformacion
                      key={carac.id}
                      nombreCampo={carac.caracteristicacritica?.etiqueta}
                    >
                      {carac.valor || "-"}
                    </CampoInformacion>
                  ))}
                </div>
              </>
            )}
          </div>
        );
      })}
    </>
  );
}
